import { generateHTML, generateJSON, JSONContent } from '@tiptap/core';
import { gql } from 'apollo-angular';
import { ANNOUNCEMENT_RICH_TEXT_EXTENSIONS, isEmptyRichText } from './announcement-rich-text';
import { AnnouncementContentEntry } from './graphql';

// #743-Nachzug: maschinelle Vorbefuellung der Sprachfassungen (Client-Dok §8.5).
//
// `translateTexts` ist ein GENERISCHER Endpunkt: er weiss nichts von Ankuendigungen, sondern
// uebersetzt benannte Textsegmente. Der Ankuendigungs-Editor ist sein erster Aufrufer, nicht sein
// Gegenstand. Deshalb steht er in einer eigenen Datei und nicht in graphql.ts.
//
// Transportformat ist HTML, NICHT ProseMirror: das Frontend serialisiert seinen Editor-Baum vor dem
// Aufruf und parst die Antwort wieder ein, der Server konvertiert nichts. Beide Richtungen laufen
// ueber ANNOUNCEMENT_RICH_TEXT_EXTENSIONS — und DAS ist die eigentliche Absicherung dieses Wegs:
// was der Uebersetzungsdienst an unbekannter Auszeichnung zurueckschickt, faellt beim Einlesen
// heraus, statt beim Speichern eine Serverablehnung zu werden. Serverseitig prueft zusaetzlich
// HtmlTagInventory, ob die Uebersetzung dieselben Tags traegt wie die Vorlage, und meldet sonst
// UNSUPPORTED_MARKUP.
//
// Das Ergebnis ist ein ENTWURF, kein gespeicherter Inhalt: es landet im Editor und durchlaeuft beim
// Speichern dieselbe Allow-List-Pruefung wie jeder handgeschriebene Text.

/** Frei gewaehlte Segmentschluessel — der Server gibt sie unveraendert zurueck. */
export const TRANSLATION_SEGMENT_TITLE = 'title';
export const TRANSLATION_SEGMENT_BODY = 'body';

/**
 * Gruende eines fehlgeschlagenen Ziels. `reason` ist der Wert, auf den der Client verzweigt —
 * NICHT `message` (Client-Dok §8.5): die Meldung ist deutscher Servertext und darf sich aendern.
 */
export type TranslationFailureReason =
  'NOT_CONFIGURED' | 'QUOTA_EXCEEDED' | 'PROVIDER_ERROR' | 'TIMEOUT' | 'UNSUPPORTED_MARKUP';

const KNOWN_FAILURE_REASONS: readonly string[] = [
  'NOT_CONFIGURED',
  'QUOTA_EXCEEDED',
  'PROVIDER_ERROR',
  'TIMEOUT',
  'UNSUPPORTED_MARKUP'
];

export interface TranslationSegmentInput {
  key: string;
  text: string;
  format: 'PLAIN' | 'HTML';
}

export interface TranslateTextsInput {
  sourceLocale: string;
  targetLocales: string[];
  segments: TranslationSegmentInput[];
}

export interface TranslatedSegment {
  key: string;
  text: string;
}

export interface LocaleTranslation {
  locale: string;
  segments: TranslatedSegment[];
}

export interface TranslationFailure {
  locale: string;
  reason: string;
  message: string;
}

/** Eine Sprache steht in GENAU EINER der beiden Listen, nie in beiden. */
export interface TranslationResult {
  translations: LocaleTranslation[];
  failures: TranslationFailure[];
}

export interface TranslateTextsResponse {
  translateTexts: TranslationResult;
}

/**
 * Gated auf dieselbe Plattform-Rolle wie die uebrigen Ankuendigungs-Operationen — wer sie aufrufen
 * darf, verbraucht unser Uebersetzungsguthaben.
 *
 * Teilerfolg ist der NORMALFALL: einzelne Zielsprachen koennen scheitern, waehrend die uebrigen
 * durchkommen. Deshalb werden beide Listen abgefragt, immer.
 */
export const TRANSLATE_TEXTS_MUTATION = gql`
  mutation translateTexts($input: TranslateTextsInput!) {
    translateTexts(input: $input) {
      translations {
        locale
        segments {
          key
          text
        }
      }
      failures {
        locale
        reason
        message
      }
    }
  }
`;

/**
 * Die Segmente einer Quellsprache: Titel als Klartext, Body als HTML.
 *
 * Das Body-Segment faellt WEG, wenn kein Text da ist. Der Server lehnt ein Segment mit leerem Text
 * ab („Segment 'body': Text darf nicht leer sein") und verwuerfe damit die ganze Uebersetzung — nur
 * weil eine reine Titelmeldung („Wartung heute 22:00") keinen Body hat.
 */
export function buildTranslationSegments(entry: AnnouncementContentEntry): TranslationSegmentInput[] {
  const segments: TranslationSegmentInput[] = [
    { key: TRANSLATION_SEGMENT_TITLE, text: entry.title, format: 'PLAIN' }
  ];
  if (!isEmptyRichText(entry.body)) {
    segments.push({
      key: TRANSLATION_SEGMENT_BODY,
      text: generateHTML(entry.body as JSONContent, ANNOUNCEMENT_RICH_TEXT_EXTENSIONS),
      format: 'HTML'
    });
  }
  return segments;
}

/**
 * Die Antwort einer Sprache zurueck in eine Sprachfassung.
 *
 * generateJSON laeuft mit DERSELBEN Allow-List wie der Editor — was ausserhalb liegt, faellt hier
 * heraus. Der try/catch entspricht renderRich() im Stream: ein Antwort-HTML, das unser Schema nicht
 * abbilden kann, darf den Tab nicht sprengen. Dann bleibt der Body leer, und der Autor sieht, dass
 * dort etwas fehlt — besser als ein zerlegter Editor.
 */
export function contentEntryFromSegments(segments: TranslatedSegment[]): AnnouncementContentEntry {
  const byKey = new Map((segments ?? []).map((segment) => [segment.key, segment.text]));
  const html = byKey.get(TRANSLATION_SEGMENT_BODY);
  let body: JSONContent | null = null;
  if (html) {
    try {
      body = generateJSON(html, ANNOUNCEMENT_RICH_TEXT_EXTENSIONS);
    } catch {
      body = null;
    }
  }
  // Leerer String, nicht null: der Wert geht in die Formularbindung, und der Server lehnt eine
  // Sprachfassung ohne Titel ab.
  return { title: byKey.get(TRANSLATION_SEGMENT_TITLE) ?? '', body };
}

/**
 * i18n-Schluessel je Grund, oder `null` fuer einen unbekannten sechsten Wert — dann zeigt die
 * Oberflaeche die woertliche Servermeldung. Dieselbe Toleranz wie announcementStatusLabelKey
 * gegenueber einem neuen Enum-Wert vor dem naechsten Web-Release.
 */
export function translationFailureKey(reason: string | null | undefined): string | null {
  return reason && KNOWN_FAILURE_REASONS.includes(reason)
    ? `core.announcement.form.translationFailure.${reason}`
    : null;
}
